import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import Footer from '../Footer';
import styles from './ContactUs.module.css';

import StudentNav from '../Navbars/StudentNav';
import AdminNav from '../Navbars/AdminNav';
import TeacherNav from '../Navbars/TeacherNav';
import GuestNav from '../Navbars/GuestNav';
import contactIcon from '../../assets/contacticon.png';
import contactIcon1 from '../../assets/contacticon1.png';
import submitIcon from '../../assets/submit.png';
import emailjs from '@emailjs/browser';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';

const ContactUs = () => {
  const form = useRef();
  const { isAdmin, isTeacher, isStudent, isGuest, setUserRole, setUserInfo, userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  const countryOptions = useMemo(() => countryList().getData(), []);

  const [formData, setFormData] = useState({
    name: '',
    childAge: '',
    email: '',
    whatsappNumber: '',
    country: '',
    message: '',
    acceptTerms: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}`, {
          credentials: 'include',
        });
        const userInfo = await response.json();
        setUserInfo(userInfo);
        if (userInfo?.usertype) {
          setUserRole(userInfo.usertype);
        }
      } catch (err) {
        console.error('Error in useEffect:', err);
      }
    };
    fetchData();
  }, [setUserInfo, setUserRole]);

  const onLogout = () => {
    fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/logout`, {
      credentials: 'include',
      method: 'POST',
    });
    setUserInfo(null);
    setUserRole('guest');
    navigate('/');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.childAge || isNaN(formData.childAge) || formData.childAge <= 0)
      newErrors.childAge = 'Please enter a valid age';
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      newErrors.email = 'Please enter a valid email';
    if (!formData.whatsappNumber.match(/^\d{10,15}$/))
      newErrors.whatsappNumber = 'Please enter a valid phone number (10-15 digits)';
    if (!formData.country) newErrors.country = 'Please select a country';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (!formData.acceptTerms) newErrors.acceptTerms = 'You must accept the terms and conditions';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Prepare the data for EmailJS
      const templateParams = {
        user_name: formData.name,
        age_child: formData.childAge,
        user_number: formData.whatsappNumber,
        user_email: formData.email,
        country: formData.country,
        message: formData.message
      };

      emailjs.sendForm('service_p478mti', 'template_za7k209', form.current, 'P0xsNsNTFQBlZTnEa')
        .then((result) => {
          console.log(result.text);
          toast.success('Message sent successfully!', {
            position: 'top-center',
            autoClose: 3000,
          });
          // Reset form after successful submission
          setFormData({
            name: '',
            childAge: '',
            email: '',
            whatsappNumber: '',
            country: '',
            message: '',
            acceptTerms: false,
          });
          setErrors({});
        })
        .catch((error) => {
          console.log(error.text);
          toast.error('Failed to send message. Please try again.', {
            position: 'top-center',
            autoClose: 3000,
          });
        });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Map EmailJS template names back to our form state names
    const stateNameMap = {
      'user_name': 'name',
      'age_child': 'childAge',
      'user_email': 'email',
      'user_number': 'whatsappNumber',
      'message': 'message'
    };

    const stateName = stateNameMap[name] || name;
    
    setFormData((prev) => ({
      ...prev,
      [stateName]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCountryChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      country: selectedOption ? selectedOption.label : '',
    }));
  };

  const username = userInfo?.username;
  const avatar = userInfo?.avatar;

  return (
    <>
      <Helmet>
        <title>Contact Us - Polymath Kids</title>
        <meta name="description" content="Get in touch with the Polymath Kids team for questions, feedback, or assistance." />
        <meta name="keywords" content="Polymath Kids, contact us, education, learning, support" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {username ? (
        <>
          {isAdmin && <AdminNav username={username} avatar={avatar} onLogout={onLogout} />}
          {isTeacher && <TeacherNav username={username} avatar={avatar} onLogout={onLogout} />}
          {isStudent && <StudentNav username={username} avatar={avatar} onLogout={onLogout} />}
        </>
      ) : (
        isGuest && <GuestNav />
      )}

      <section className={styles.contactHeader}>
        <div className={styles.contactHeaderContent}>
          <div className={styles.headerIconContainer}>
            <img src={contactIcon} alt="Contact Icon Left" className={`${styles.contactImage} ${styles.contactImageLeft}`} />
            <div className={styles.headerText}>
              <h1>
                Get in Touch with the <br />
                <span className={styles.highlight}>Polymath Kids Team</span>
              </h1>
              <p>
                Have questions, feedback, or need assistance? We're here to help you every step of the way—whether you're a parent, educator, or curious learner. Reach out to us anytime and we'll get back to you as soon as possible. Your journey with Polymath-Kids.com matters to us, and we're just a message away.
              </p>
            </div>
            <img src={contactIcon1} alt="Contact Icon Right" className={`${styles.contactImage} ${styles.contactImageRight}`} />
          </div>
        </div>
      </section>

      <section className={styles.contactForm}>
        <div className={styles.contactFormContent}>
          <h2>
            <span className={styles.formTitlePart1}>Need Help?</span>{' '}
            <span className={styles.formTitlePart2}>Contact Us Anytime</span>
          </h2>
          <form onSubmit={handleSubmit} noValidate ref={form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="user_name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? `${styles.error}` : ''}
                />
                {errors.name && <span className={styles.errorMessage}>{errors.name}</span>}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="childAge">Age of Child</label>
                <input
                  type="number"
                  id="childAge"
                  name="age_child"
                  value={formData.childAge}
                  onChange={handleInputChange}
                  className={errors.childAge ? `${styles.error}` : ''}
                />
                {errors.childAge && <span className={styles.errorMessage}>{errors.childAge}</span>}
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="user_email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? `${styles.error}` : ''}
                />
                {errors.email && <span className={styles.errorMessage}>{errors.email}</span>}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="whatsappNumber">WhatsApp Number</label>
                <input
                  type="tel"
                  id="whatsappNumber"
                  name="user_number"
                  value={formData.whatsappNumber}
                  onChange={handleInputChange}
                  className={errors.whatsappNumber ? `${styles.error}` : ''}
                />
                {errors.whatsappNumber && <span className={styles.errorMessage}>{errors.whatsappNumber}</span>}
              </div>
            </div>
            <div className={styles.formGroupFullWidth}>
              <label htmlFor="country">Choose Your Country</label>
              <Select
                id="country"
                name="country"
                options={countryOptions}
                value={countryOptions.find(option => option.label === formData.country)}
                onChange={handleCountryChange}
                className={errors.country ? `${styles.error}` : ''}
                placeholder="Choose Your Country"
              />
              {errors.country && <span className={styles.errorMessage}>{errors.country}</span>}
            </div>
            <div className={styles.formGroupFullWidth}>
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                placeholder="Type your message"
                value={formData.message}
                onChange={handleInputChange}
                className={errors.message ? `${styles.error}` : ''}
              />
              {errors.message && <span className={styles.errorMessage}>{errors.message}</span>}
            </div>
            <div className={styles.formGroupTerms}>
              <label>
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                />
                I accept all the terms and conditions.
              </label>
              {errors.acceptTerms && <span className={styles.errorMessage}>{errors.acceptTerms}</span>}
            </div>
            <div className={styles.submitContainer}>
              <img
                src={submitIcon}
                alt="Submit"
                className={styles.submitIcon}
                onClick={handleSubmit}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
              />
            </div>
          </form>
        </div>
      </section>

      <Footer />
      <ToastContainer />
    </>
  );
};

export default ContactUs;